import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { pickBy } from 'lodash';
import { updateSearchFilters as updateSearchFiltersAction } from 'store/modules/search';
import { resolveSearchNavigation } from 'utils/search';

/**
 * Builds the `onSearch` handler shared by SearchPage and NewSearchPage.
 *
 * Updates the store with the merged filter params, then navigates. On amenity
 * LSA pages the amenity lives in the URL path, not the query string, so
 * clearing it must redirect to the city CLP path; otherwise the route
 * re-seeds the amenity (SF-21011).
 *
 * @param {Object} args
 * @param {Object} args.searchParams Current search params from the store.
 * @param {string} [args.cityUrl]    City CLP path used as the redirect target.
 * @returns {(filterValues: Object, delayFiltering?: boolean) => void}
 */
export default function useSearchNavigation({ searchParams, cityUrl }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	return useCallback(
		(filterValues, delayFiltering = false) => {
			// page: 1 must come AFTER filterValues so pagination resets when
			// filters change (SF-19283).
			const params = pickBy({
				...searchParams,
				...filterValues,
				page: 1,
			});
			dispatch(updateSearchFiltersAction(params));
			if (!delayFiltering) {
				navigate(
					resolveSearchNavigation({
						pathname: location.pathname,
						params,
						cityUrl,
					}),
				);
			}
		},
		[searchParams, dispatch, navigate, location.pathname, cityUrl],
	);
}
